import { lang } from '@umu/ukit-lang';
import { SnackBar } from '@umu/umu-design';
import { useAsyncEffect } from 'ahooks';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { getResourceCertificate, receiveCertificate, setCertificatePermission } from '../api/server';
import { jumpToManageCertificate } from '../dataFlow';
import { CertificatePermission, type IAiCertificateItem, RecognizeObjectType } from '../interface/api';
import type { IUseCaetConfigReturn } from '../interface/caetConfig';
import type {
    ICompleteFooterButtonsConfig,
    IUseCaetCompleteParams,
    IUseCaetCompleteReturn,
} from '../interface/complete';
import { downloadCaetCertificate } from '../utils';
import { useCaetConfig } from './useCaetConfig';
/**
 * CaetComplete 组件逻辑层 Hook
 */
export const useCaetComplete = (params: IUseCaetCompleteParams): IUseCaetCompleteReturn & IUseCaetConfigReturn => {
    const {
        id = '',
        email = '',
        onClose,
        ref,
        showContentInfo = true,
        type = RecognizeObjectType.COURSE,
        footerButtonsConfig,
        receive = false,
        footerRender,
    } = params;
    const receivedRef = useRef<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const refCertificateInfo = useRef<Record<string, IAiCertificateItem>>({});
    const [certificateId, setCertificateId] = useState<string>(params?.certificateId || '');
    const caetConfig = useCaetConfig({
        id: id,
        certificateId: certificateId,
        objType: type,
        isComplete: true,
    });
    const { reload: reloadCertificateConfig } = caetConfig;
    // 状态管理
    const [isPublic, setIsPublic] = useState<boolean>(true);
    // 使用 ref 记录 isPublic 的原始值
    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
        getPermissionStatus: () => isPublic,
    }));
    const onPermissionChange = useCallback(
        async (value: boolean): Promise<void> => {
            try {
                // 参数检查
                if (!id || !type) {
                    throw new Error('id and type are required');
                }

                let tmpCertificateId = certificateId;
                if (!tmpCertificateId) {
                    const certificateInfo = await getCertificateInfo();
                    if (!certificateInfo) {
                        return;
                    }
                    tmpCertificateId = certificateInfo.id;
                }

                const { isSuccess, message = '' } = await setCertificatePermission({
                    id: tmpCertificateId,
                    permission: value ? CertificatePermission.PUBLIC : CertificatePermission.PRIVATE,
                });

                SnackBar.show({
                    message: isSuccess ? lang('证书权限设置成功') : message || lang('证书权限设置失败'),
                });

                setIsPublic(value);
                if (tmpCertificateId !== certificateId) {
                    setCertificateId(tmpCertificateId);
                }
            } catch (_error) {
                // 错误处理
            }
        },
        [certificateId, id, type],
    );
    /**
     * 权限切换处理函数
     */
    const handlePermissionChange = useCallback(
        (checked: boolean) => {
            onPermissionChange(checked);
        },
        [onPermissionChange],
    );
    const getCertificateInfo = useCallback(async (): Promise<IAiCertificateItem | null> => {
        // 参数检查
        if (!id || !type) {
            return null;
        }

        try {
            // 先从缓存中获取证书信息
            let certificateInfo = refCertificateInfo.current[id];

            if (!certificateInfo) {
                // 如果缓存中没有，则从服务器获取
                const certificateInfoTmp = await getResourceCertificate({
                    obj_type: type,
                    obj_id: id,
                });

                // 空值判断
                if (!certificateInfoTmp) {
                    return null;
                }

                // 将获取到的证书信息存储到缓存中
                refCertificateInfo.current[id] = certificateInfoTmp;
                certificateInfo = certificateInfoTmp;
            }

            return certificateInfo;
        } catch (_error) {
            // 错误处理
            return null;
        }
    }, [id, type]);
    /**
     * 下载证书处理函数
     */
    const handleDownload = useCallback(async (): Promise<void> => {
        if (!id || !type || isDownloading) {
            return;
        }
        const certificateInfo = await getCertificateInfo();
        if (!certificateInfo) {
            return;
        }
        setIsDownloading(true);
        // 使用可选链操作符
        const caetNumber = certificateInfo?.caet_number;
        downloadCaetCertificate({
            id: id,
            certificate_id: certificateInfo.id,
            fileName: caetNumber,
        }).finally(() => {
            setIsDownloading(false);
        });
    }, [id, certificateId, type]);

    /**
     * 确认处理函数
     */
    const handleConfirm = useCallback(() => {
        onClose?.();
    }, [onClose]);
    /**
     * 管理证书处理函数
     */
    const handleManage = useCallback(async (): Promise<void> => {
        handleConfirm();
        jumpToManageCertificate(certificateId);
    }, [certificateId, handleConfirm]);

    // 默认按钮配置
    const defaultFooterButtonsConfig: ICompleteFooterButtonsConfig = {
        download: {
            visible: true,
            type: 'ghost-grey',
            text: lang('下载证书'),
            onClick: handleDownload,
        },
        manage: {
            visible: true,
            type: 'ghost-grey',
            text: lang('管理证书'),
            onClick: handleManage,
        },
        confirm: {
            visible: true,
            type: 'primary',
            text: lang('确认'),
            onClick: handleConfirm,
        },
    };

    // 合并用户配置和默认配置，确保所有默认值都被正确设置
    const mergedFooterButtonsConfig: ICompleteFooterButtonsConfig = {
        download: {
            visible: footerButtonsConfig?.download?.visible ?? defaultFooterButtonsConfig.download.visible,
            type: footerButtonsConfig?.download?.type || defaultFooterButtonsConfig.download.type,
            text: footerButtonsConfig?.download?.text || defaultFooterButtonsConfig.download.text,
            onClick: footerButtonsConfig?.download?.onClick || defaultFooterButtonsConfig.download.onClick,
        },
        manage: {
            visible: footerButtonsConfig?.manage?.visible ?? defaultFooterButtonsConfig.manage.visible,
            type: footerButtonsConfig?.manage?.type || defaultFooterButtonsConfig.manage.type,
            text: footerButtonsConfig?.manage?.text || defaultFooterButtonsConfig.manage.text,
            onClick: footerButtonsConfig?.manage?.onClick || defaultFooterButtonsConfig.manage.onClick,
        },
        confirm: {
            visible: footerButtonsConfig?.confirm?.visible ?? defaultFooterButtonsConfig.confirm.visible,
            type: footerButtonsConfig?.confirm?.type || defaultFooterButtonsConfig.confirm.type,
            text: footerButtonsConfig?.confirm?.text || defaultFooterButtonsConfig.confirm.text,
            onClick: footerButtonsConfig?.confirm?.onClick || defaultFooterButtonsConfig.confirm.onClick,
        },
    };
    useAsyncEffect(async () => {
        if (receive && !receivedRef.current) {
            await receiveCertificate({
                obj_id: id,
                obj_type: type,
            });
            reloadCertificateConfig();
            receivedRef.current = true;
        }
    }, [receive]);
    return {
        isPublic,
        showEmail: email.trim() !== '',
        email,
        isDownloading,
        ...caetConfig,
        handlePermissionChange,
        handleDownload,
        handleManage,
        handleConfirm,
        showContentInfo,
        footerButtonsConfig: mergedFooterButtonsConfig,
        footerRender,
    };
};
